import axios from "axios";
import { useEffect, useState } from "react";
import Chart from "react-google-charts";
import { API_ENDPOINT, HEADER_TOKEN, MONTHS } from "../../constants/constants";
import { deleteToken, getHeader } from "../../utils/util";

function fmtDate(d) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return ('0' + d.getDate()).slice(-2) + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
}

const chartOptions = (leftTitle, rightTitle) => ({
  legend: "none",
  backgroundColor: "transparent",
  fontName: "Archivo",
  chartArea: { left: 44, top: 12, width: "82%", height: "74%" },
  hAxis: {
    textStyle: { fontSize: 10, color: "#7d7979" },
    baselineColor: "#d7d3d3",
    gridlines: { color: "transparent" },
  },
  vAxes: {
    0: { textStyle: { fontSize: 10, color: "#9b9797" }, gridlines: { color: "#d7d3d3", count: 5 }, minValue: 0 },
    1: { textStyle: { fontSize: 10, color: "#9b9797" }, gridlines: { color: "transparent" }, minValue: 0 },
  },
  series: {
    0: { targetAxisIndex: 0, type: "bars", color: "#444141" },
    1: { targetAxisIndex: 1, type: "line", color: "#ec3013", lineWidth: 2.5, pointSize: 5 },
  },
  bar: { groupWidth: "55%" },
  height: 250,
});

const Legend = ({ bar, line }) => (
  <div style={{ display: "flex", gap: 18, marginBottom: 6, fontSize: 12 }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 11, height: 11, background: "var(--color-neutral-800)", display: "inline-block" }}></span>{bar}
    </span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 14, height: 3, background: "var(--color-accent)", display: "inline-block" }}></span>{line}
    </span>
  </div>
);

const Dashboard = () => {
  const [totalTest, setTotalTest] = useState("…");
  const [activeTrainers, setActiveTrainers] = useState("…");
  const [expiredTrainers, setExpiredTrainers] = useState("…");
  const [totalMockTest, setTotalMockTest] = useState("…");
  const currentYear = new Date().getFullYear();
  const [assessmentsOverview, setAssessmentsOverview] = useState([]);
  const [mocktestsOverview, setMockTestsOverview] = useState([]);
  const [aOcurrentYear, setAoCurrentYear] = useState(currentYear);
  const [mOcurrentYear, setMoCurrentYear] = useState(currentYear);
  const [recent, setRecent] = useState([]);
  const pastYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const logout = () => { deleteToken(); document.location.href = "/login"; };

  function getTrainersOverview() {
    axios.post(API_ENDPOINT + "admin/trainers-overview", { year: aOcurrentYear }, getHeader()).then((res) => {
      let d = res.data;
      if (d.success) {
        let asA = [["Months", "New Trainers", "Trainers with Assessment"]];
        MONTHS.forEach((m, i) => {
          let foundO = d.message.find((r) => r._id === i + 1);
          asA.push(foundO ? [m.slice(0, 3), foundO.newTrainerCount, foundO.trainersWithAssessments || 0] : [m.slice(0, 3), 0, 0]);
        });
        setAssessmentsOverview(asA);
      }
    });
  }

  function getMocktestsOverview() {
    axios.post(API_ENDPOINT + "admin/mocktests-overview", { year: mOcurrentYear }, getHeader()).then((res) => {
      let d = res.data;
      if (d.success) {
        let asA = [["Months", "Total Users", "Total Mocktests"]];
        MONTHS.forEach((m, i) => {
          let foundO = d.message.find((r) => r._id === i + 1);
          asA.push(foundO ? [m.slice(0, 3), foundO.uniqueCandidatesCount, foundO.numMocktests || 0] : [m.slice(0, 3), 0, 0]);
        });
        setMockTestsOverview(asA);
      } else if (d.auth === false) {
        logout();
      }
    });
  }

  function getAssessmentNum() {
    axios.get(API_ENDPOINT + "admin/get-total-test", getHeader()).then((res) => { if (res.data.success) setTotalTest(res.data.message); });
  }
  function getTotalTrainers() {
    axios.get(API_ENDPOINT + "admin/get-total-trainers", getHeader()).then((res) => {
      let d = res.data;
      if (d.success) { setActiveTrainers(d.activeTrainers); setExpiredTrainers(d.expiredTrainers); }
    });
  }
  function getTotalMockTest() {
    axios.get(API_ENDPOINT + "admin/get-total-mock-test", getHeader()).then((res) => { if (res.data.success) setTotalMockTest(res.data.message); });
  }
  function getRecent() {
    axios.post(API_ENDPOINT + "sam/get-mocktests", {}, HEADER_TOKEN).then((res) => {
      if (res.data.success) setRecent((res.data.message || []).slice(0, 5));
    });
  }

  useEffect(() => { getAssessmentNum(); getTotalTrainers(); getTotalMockTest(); getRecent(); }, []);
  useEffect(() => { getTrainersOverview(); }, [aOcurrentYear]);
  useEffect(() => { getMocktestsOverview(); }, [mOcurrentYear]);

  const stats = [
    { label: "Active Trainers", value: activeTrainers, delta: "+12%", sub: "vs last 30d" },
    { label: "Inactive Trainers", value: expiredTrainers, delta: "+5%", sub: "vs last 30d" },
    { label: "Total Tests", value: totalTest, delta: "+50%", sub: "vs last 30d" },
    { label: "Total Mock Tests", value: totalMockTest, delta: "+4%", sub: "vs last 30d" },
  ];

  const YearSelect = ({ value, onChange }) => (
    <select className="input" style={{ width: "auto", minHeight: 32, fontSize: 13 }} value={value} onChange={onChange}>
      {pastYears.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  );

  const goMock = () => { document.location.href = "/mock-test"; };

  return (
    <div>
      {/* STAT CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
        {stats.map((s, i) => (
          <div key={i} style={{ background: "var(--color-surface)", padding: "18px 18px 16px", borderTop: "3px solid var(--color-divider)" }}>
            <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-accent)" }}>{s.label}</div>
            <div style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 38, lineHeight: 1.05, marginTop: 6 }}>{s.value}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-accent)" }}>{s.delta}</span>
              <span style={{ fontSize: 12 }} className="text-muted">{s.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* CHARTS */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
        <div style={{ background: "var(--color-surface)", padding: 18 }}>
          <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-accent)" }}>Trainers</div>
              <h4 style={{ margin: "2px 0 0" }}>Assessment activity · {aOcurrentYear}</h4>
            </div>
            <YearSelect value={aOcurrentYear} onChange={(e) => setAoCurrentYear(e.target.value)} />
          </div>
          <Legend bar="New trainers" line="With assessment" />
          {assessmentsOverview.length > 0 && (
            <Chart chartType="ComboChart" width="100%" height="250px" data={assessmentsOverview} options={chartOptions()} />
          )}
        </div>
        <div style={{ background: "var(--color-surface)", padding: 18 }}>
          <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-accent)" }}>Engagement</div>
              <h4 style={{ margin: "2px 0 0" }}>Mock test volume · {mOcurrentYear}</h4>
            </div>
            <YearSelect value={mOcurrentYear} onChange={(e) => setMoCurrentYear(e.target.value)} />
          </div>
          <Legend bar="Total users" line="Mock tests" />
          {mocktestsOverview.length > 0 && (
            <Chart chartType="ComboChart" width="100%" height="250px" data={mocktestsOverview} options={chartOptions()} />
          )}
        </div>
      </div>

      {/* RECENT MOCK TESTS */}
      <div style={{ background: "var(--color-surface)", padding: 18, marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <h4 style={{ margin: 0, flex: 1 }}>Recent mock tests</h4>
          <a href="/mock-test" onClick={(e) => { e.preventDefault(); goMock(); }} style={{ fontSize: 13 }}>View all</a>
        </div>
        <table className="table">
          <thead>
            <tr><th>Assessment</th><th>Category</th><th>Questions</th><th>Duration</th><th>Topics</th><th>Created</th></tr>
          </thead>
          <tbody>
            {recent.map((r, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{r.title}</td>
                <td className="text-muted">{r.category}</td>
                <td>{r.numQns}</td>
                <td>{r.duration ? `${r.duration} min` : "—"}</td>
                <td>{r.numTopics}</td>
                <td className="text-muted">{r.date ? fmtDate(new Date(r.date)) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default Dashboard;
